import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Card, CardContent } from '@mui/material'
import LinkedInIcon from '@mui/icons-material/LinkedIn'
import TwitterIcon from '@mui/icons-material/Twitter'
import LanguageIcon from '@mui/icons-material/Language'
import SEO from '../utils/seo'
import Loading from '../components/Loading'
import EmptyState from '../components/EmptyState'
import { colors } from '../styles/theme'

const API_URL = import.meta.env.VITE_API_URL || '/api'

const Guests = () => {
  const [episodes, setEpisodes] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchEpisodes = async () => {
      try {
        const response = await fetch(`${API_URL}/episodes`)
        const data = await response.json()
        setEpisodes(data)
      } catch (error) {
        console.error('Error fetching episodes:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchEpisodes()
  }, [])

  const guests = useMemo(() => {
    const guestMap = new Map()
    episodes.forEach((episode) => {
      if (episode.guest) {
        if (!guestMap.has(episode.guest.name)) {
          guestMap.set(episode.guest.name, {
            ...episode.guest,
            episodes: [],
          })
        }
        guestMap.get(episode.guest.name).episodes.push({
          slug: episode.slug,
          title: episode.title,
          number: episode.number,
        })
      }
    })
    return Array.from(guestMap.values())
  }, [episodes])

  if (loading) return <Loading />

  return (
    <>
      <SEO
        title="Guests"
        description="Meet the engineers, architects, and builders who have shared their insights on Prompted Thoughts."
      />

      <div className="section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-off-white mb-4">Featured Guests</h1>
            <p className="text-lg text-gray-400">
              Engineers, architects, and builders shaping the future of AI-native software
            </p>
          </motion.div>

          {guests.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {guests.map((guest, index) => (
                <motion.div
                  key={guest.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <Card
                    sx={{
                      backgroundColor: colors.bgCard,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: 'var(--shadow-card-hover)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 4, flexGrow: 1 }}>
                      <div className="flex flex-col items-center text-center">
                        <img
                          src={guest.photo || '/images/placeholder-guest.jpg'}
                          alt={guest.name}
                          className="w-32 h-32 rounded-full object-cover mb-4"
                        />
                        <h3 className="text-xl font-bold text-off-white mb-1">{guest.name}</h3>
                        <p className="text-cyan text-sm mb-2">{guest.role}</p>
                        <p className="text-gray-400 text-sm mb-4">{guest.company}</p>

                        {guest.links && (
                          <div className="flex gap-2 mb-4">
                            {guest.links.map((link) => (
                              <a
                                key={link.label}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-400 hover:text-cyan transition-colors"
                                aria-label={link.label}
                              >
                                {link.label === 'LinkedIn' && <LinkedInIcon />}
                                {link.label === 'Twitter' && <TwitterIcon />}
                                {(link.label === 'Website' || link.label === 'Blog') && (
                                  <LanguageIcon />
                                )}
                              </a>
                            ))}
                          </div>
                        )}

                        <div className="w-full pt-4 border-t border-slate">
                          <p className="text-sm text-gray-400 mb-2">
                            {guest.episodes.length} episode{guest.episodes.length !== 1 ? 's' : ''}:
                          </p>
                          <div className="space-y-2">
                            {guest.episodes.map((ep) => (
                              <Link
                                key={ep.slug}
                                to={`/episodes/${ep.slug}`}
                                className="block text-sm text-cyan hover:text-cyan/80 gradient-underline"
                              >
                                EP {String(ep.number).padStart(2, '0')}
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            <EmptyState
              title="No guests yet"
              message="Featured guests will appear here once episodes are published."
            />
          )}
        </div>
      </div>
    </>
  )
}

export default Guests
